import {
    useMemo,
} from 'react'
import {
    useTranslation,
} from 'react-i18next'
import flatten from 'lodash/flatten'
import {
    ReactComponent as OrdersIcon,
} from 'app/shared-components/images/icons/order.svg'
import {
    ReactComponent as ContainersIcon,
} from 'app/shared-components/images/icons/container.svg'
import {
    ReactComponent as SensorsIcon,
} from 'app/shared-components/images/icons/sensors.svg'
import {
    ReactComponent as FinanceIcon,
} from 'app/shared-components/images/icons/finance.svg'
import {
    ReactComponent as LocationIcon,
} from 'app/shared-components/images/icons/location.svg'
import {
    ReactComponent as LanesIcon,
} from 'app/shared-components/images/icons/lanes.svg'
import {
    ReactComponent as TransportPlannerIcon,
} from 'app/shared-components/images/icons/transport_planner.svg'
import {
    ReactComponent as RelationshipsManagementIcon,
} from 'app/shared-components/images/icons/relationships_management.svg'
import {
    ReactComponent as AccountManagementIcon,
} from 'app/shared-components/images/icons/account_management.svg'
import {
    ReactComponent as DamagesIcon,
} from 'app/shared-components/images/icons/damages.svg'
import {
    ReactComponent as ForecastIcon,
} from 'app/shared-components/images/icons/forecast.svg'
import {
    ReactComponent as RateCardsIcon,
} from 'app/shared-components/images/icons/rate_cards.svg'

import authRoles from 'app/authRoles'
import {
    ActiveTab,
} from 'app/MainMenu/MainMenu.types'
import {
    App,
    AccountManagementDomains,
    ContactManagementDomains,
    ContainerManagementDomains,
    DamageTrackingDomains,
    FinanceDomains,
    ForecastDomains,
    OrderManagementDomains,
    PricingDomains,
    RatesDomains,
    RelationshipsManagementDomains,
    TransportPlannerDomains,
} from 'app/enums/domain.enums'
import useContactManagementRoutes from 'app/SkyNetRoutes/hooks/useContactManagementRoutes'
import useRelationshipsManagementRoutes
    from 'app/SkyNetRoutes/hooks/useRelationshipsManagementRoutes'
import useAccountManagementRoutes from 'app/SkyNetRoutes/hooks/useAccountManagementRoutes'
import usePricingRoutes from 'app/SkyNetRoutes/hooks/usePricingRoutes'
import useForecastRoutes from 'app/SkyNetRoutes/hooks/useForecastRoutes'
import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'
import useContainerManagementRoutes from 'app/SkyNetRoutes/hooks/useContainerManagementRoutes'
import useLoggerRoutes from 'app/SkyNetRoutes/hooks/useLoggerRoutes'
import useGatewaysRoutes from 'app/SkyNetRoutes/hooks/useGatewayRoutes'
import useDeploymentRoutes from 'app/SkyNetRoutes/hooks/useDeploymentRoutes'
import useFinanceRoutes from 'app/SkyNetRoutes/hooks/useFinanceRoutes'
import useTransportPlannerRoutes from 'app/SkyNetRoutes/hooks/useTransportPlannerRoutes'
import useDamageTrackingRoutes from 'app/SkyNetRoutes/hooks/useDamageTrackingRoutes'
import useRatesRoutes from 'app/SkyNetRoutes/hooks/useRatesRoutes'

const [accountReadAll] = authRoles.account_read_all

export default function useMainMenuConfig(): ActiveTab[] {
    const {
        t,
    } = useTranslation()
    const ContactManagementRoutes = useContactManagementRoutes()
    const RelationshipsManagementRoutes = useRelationshipsManagementRoutes()
    const AccountManagementRoutes = useAccountManagementRoutes()
    const PricingRoutes = usePricingRoutes()
    const ForecastRoutes = useForecastRoutes()
    const OrderManagementRoutes = useOrderManagementRoutes()
    const ContainerManagementRoutes = useContainerManagementRoutes()
    const RatesRoutes = useRatesRoutes()
    const {
        Loggers: LoggerRoutes,
    } = useLoggerRoutes()
    const {
        Gateways: GatewaysRoutes,
    } = useGatewaysRoutes()
    const {
        Deployments: DeploymentRoutes,
    } = useDeploymentRoutes()
    const FinanceRoutes = useFinanceRoutes()
    const TransportPlannerRoutes = useTransportPlannerRoutes()
    const DamageTrackingRoutes = useDamageTrackingRoutes()

    return useMemo(() => {
        return [
            {
                title: t('CONTACT_MANAGEMENT'),
                id: App.ContactManagement,
                subTitleText: t('CONTACT_MANAGEMENT_SUBTITLE'),
                icon: LocationIcon,
                className: 'iconSmall',
                links: [
                    {
                        title: t('LOCATIONS'),
                        id: ContactManagementDomains.Locations,
                        auth: authRoles.location_read,
                        url: ContactManagementRoutes.Locations.pattern,
                    },
                    {
                        title: t('BILLING_COMPANIES'),
                        id: ContactManagementDomains.BillingCompanies,
                        auth: authRoles.billing_company_read,
                        url: ContactManagementRoutes.BillingCompanies.pattern,
                    },
                    {
                        title: t('COMPANIES'),
                        id: ContactManagementDomains.Companies,
                        auth: authRoles.company_read,
                        url: ContactManagementRoutes.Companies.pattern,
                    },
                    {
                        title: t('AIRPORTS_AND_SEAPORTS'),
                        id: ContactManagementDomains.AirportsAndSeaports,
                        auth: authRoles.location_read,
                        url: ContactManagementRoutes.AirportsAndSeaports.pattern,
                    },
                    {
                        title: t('CONTACTS'),
                        id: ContactManagementDomains.Contacts,
                        auth: authRoles.location_read,
                        url: ContactManagementRoutes.Contacts.pattern,
                    },
                    {
                        title: t('COUNTRIES'),
                        id: ContactManagementDomains.Countries,
                        auth: authRoles.location_read,
                        url: ContactManagementRoutes.Countries.pattern,
                    },
                    {
                        title: t('USERS'),
                        id: ContactManagementDomains.Users,
                        auth: authRoles.user_read,
                        url: ContactManagementRoutes.Users.pattern,
                    },
                ],
            },
            {
                title: t('RELATIONSHIPS_MANAGEMENT'),
                id: App.RelationshipsManagement,
                subTitleText: t('RELATIONSHIPS_MANAGEMENT_SUBTITLE'),
                icon: RelationshipsManagementIcon,
                className: 'iconSmall',
                links: [
                    {
                        title: t('ACTIVITY_RECORDS'),
                        id: RelationshipsManagementDomains.ActivityRecords,
                        auth: [
                            ...authRoles.relationships_ops,
                            ...authRoles.relationships_sales,
                        ],
                        url: RelationshipsManagementRoutes.ActivityRecords.pattern,
                    },
                    {
                        title: t('ENGAGEMENTS'),
                        id: RelationshipsManagementDomains.Engagements,
                        auth: [...authRoles.relationships_sales],
                        url: RelationshipsManagementRoutes.Engagements.pattern,
                    },
                ],
            },
            {
                title: t('ACCOUNT_MANAGEMENT'),
                id: App.AccountManagement,
                subTitleText: t('ACCOUNT_MANAGEMENT_SUBTITLE'),
                icon: AccountManagementIcon,
                className: 'iconLarge',
                links: [
                    {
                        title: t('DASHBOARD'),
                        id: AccountManagementDomains.KamDashboard,
                        auth: authRoles.account_quote_lane_award_reject,
                        url: AccountManagementRoutes.KamDashboard.pattern,
                    },
                    {
                        title: t('ACCOUNTS'),
                        id: AccountManagementDomains.Accounts,
                        auth: [accountReadAll],
                        url: AccountManagementRoutes.Accounts.pattern,
                    },
                    {
                        title: t('REGIONAL_GROUPS'),
                        id: AccountManagementDomains.RegionalGroups,
                        auth: [accountReadAll],
                        url: AccountManagementRoutes.RegionalGroups.pattern,
                    },
                    {
                        title: t('OPPORTUNITIES'),
                        id: AccountManagementDomains.Opportunitites,
                        auth: [accountReadAll],
                        url: AccountManagementRoutes.Opportunities.pattern,
                    },
                    {
                        title: t('LANES'),
                        id: AccountManagementDomains.Lanes,
                        auth: [accountReadAll],
                        url: AccountManagementRoutes.Lanes.pattern,
                    },
                    {
                        title: t('A_QUOTES'),
                        id: AccountManagementDomains.AQuotes,
                        auth: [accountReadAll],
                        url: AccountManagementRoutes.AQuotes.pattern,
                    },
                    {
                        title: t('C_QUOTES'),
                        id: AccountManagementDomains.CQuotes,
                        auth: [accountReadAll],
                        url: AccountManagementRoutes.CQuotes.pattern,
                    },
                    {
                        title: t('SIMPLIFIED_C_QUOTES'),
                        id: AccountManagementDomains.SimplifiedCQuotes,
                        auth: [accountReadAll],
                        url: AccountManagementRoutes.SimplifiedCQuotes.pattern,
                    },
                    {
                        title: t('PRE_BOOKINGS'),
                        id: AccountManagementDomains.PreBookings,
                        auth: authRoles.pre_booking_read,
                        url: AccountManagementRoutes.PreBookings.pattern,
                    },
                ],
            },
            {
                title: t('PRICING'),
                id: App.Pricing,
                subTitleText: t('PRICING_SUBTITLE'),
                icon: LanesIcon,
                className: 'iconMedium',
                links: [
                    {
                        title: t('DASHBOARD'),
                        id: PricingDomains.PricingDashboard,
                        auth: authRoles.account_quote_lane_group,
                        url: PricingRoutes.PricingDashboard.pattern,
                    },
                    {
                        title: t('CONTRACT_BASIS'),
                        id: PricingDomains.ContractBasis,
                        auth: authRoles.contract_basis_read,
                        url: PricingRoutes.ContractBasis.pattern,
                    },
                    {
                        title: t('LANE_GROUPS'),
                        id: PricingDomains.LaneGroups,
                        auth: authRoles.contract_basis_read,
                        url: PricingRoutes.LaneGroups.pattern,
                    },
                    {
                        title: t('LANE_FEES'),
                        id: PricingDomains.LaneFees,
                        auth: authRoles.contract_basis_read,
                        url: PricingRoutes.LaneFees.pattern,
                    },
                    {
                        title: t('LANES'),
                        id: PricingDomains.Lanes,
                        auth: authRoles.contract_basis_read,
                        url: PricingRoutes.Lanes.pattern,
                    },
                    {
                        title: t('AL_LOGISTIC_PRICING'),
                        id: PricingDomains.AlLogisticPricing,
                        auth: authRoles.contract_basis_read,
                        url: PricingRoutes.AlLogisticPricing.pattern,
                    },
                    {
                        title: t('AL_LEASE_PRICING'),
                        id: PricingDomains.AlLeasePricing,
                        auth: authRoles.contract_basis_read,
                        url: PricingRoutes.AlLeasePricing.pattern,
                    },
                    {
                        title: t('A_QUOTES'),
                        id: PricingDomains.AQuotes,
                        auth: authRoles.contract_basis_read,
                        url: PricingRoutes.AQuotes.pattern,
                    },
                ],
            },
            {
                title: t('FORECAST'),
                id: App.Forecast,
                subTitleText: t('FORECAST_SUBTITLE'),
                icon: ForecastIcon,
                className: 'iconMedium',
                links: [{
                    title: t('SHORT_TERM_FORECAST'),
                    id: ForecastDomains.ShortTermForecasts,
                    auth: flatten([
                        authRoles.forecast_read,
                        authRoles.forecast_super,
                    ]),
                    url: ForecastRoutes.ShortTermForecast.pattern,
                }],
            },
            {
                title: t('ORDER_MANAGEMENT'),
                id: App.OrderManagement,
                subTitleText: t('ORDER_MANAGEMENT_SUBTITLE'),
                icon: OrdersIcon,
                links: [
                    {
                        title: t('DAILY_OPS'),
                        id: OrderManagementDomains.DailyOps,
                        auth: authRoles.order_read,
                        url: OrderManagementRoutes.DailyOps.pattern,
                    },
                    {
                        title: t('POOLING'),
                        id: OrderManagementDomains.Pooling,
                        auth: authRoles.order_read,
                        url: OrderManagementRoutes.Pooling.pattern,
                    },
                    {
                        title: t('OPEN_BOOKINGS'),
                        id: OrderManagementDomains.OpenBookings,
                        auth: authRoles.order_read,
                        url: OrderManagementRoutes.OpenBookings.pattern,
                    },
                    {
                        title: t('LANES'),
                        id: OrderManagementDomains.Lanes,
                        auth: authRoles.contract_basis_read,
                        url: OrderManagementRoutes.Lanes.pattern,
                    },
                    {
                        title: t('CUSTOMER_ORDERS'),
                        id: OrderManagementDomains.CustomerOrders,
                        auth: authRoles.order_read,
                        url: OrderManagementRoutes.CustomerOrders.pattern,
                    },
                    {
                        title: t('INTERNAL_ORDERS'),
                        id: OrderManagementDomains.InternalOrders,
                        auth: authRoles.order_read,
                        url: OrderManagementRoutes.InternalOrders.pattern,
                    },
                    {
                        title: t('FREIGHT_FORWARDERS'),
                        id: OrderManagementDomains.FreightForwarders,
                        auth: authRoles.freight_forwarder_read,
                        url: OrderManagementRoutes.FreightForwarders.pattern,
                    },
                    {
                        title: t('LONG_TERM_LEASE'),
                        id: OrderManagementDomains.LongTermLease,
                        auth: authRoles.long_term_lease_read,
                        url: OrderManagementRoutes.LongTermLease.pattern,
                    },
                    {
                        title: t('PRE_BOOKINGS'),
                        id: OrderManagementDomains.PreBookings,
                        auth: authRoles.pre_booking_read,
                        url: OrderManagementRoutes.PreBookings.pattern,
                    },
                    {
                        title: t('PROFORMA_INVOICES'),
                        id: OrderManagementDomains.ProformaInvoices,
                        auth: authRoles.proforma_rule_read,
                        url: OrderManagementRoutes.ProformaInvoices.pattern,
                    },
                ],
            },
            {
                title: t('CONTAINER_MANAGEMENT'),
                id: App.Containers,
                subTitleText: t('CONTAINER_MANAGEMENT_SUBTITLE'),
                icon: ContainersIcon,
                className: 'iconLarge',
                links: [
                    {
                        title: t('CONTAINERS'),
                        id: ContainerManagementDomains.Containers,
                        auth: authRoles.container_read,
                        url: ContainerManagementRoutes.Containers.pattern,
                    },
                    {
                        title: t('DASHBOARD'),
                        id: ContainerManagementDomains.ContainerDashboard,
                        auth: authRoles.container_read,
                        url: ContainerManagementRoutes.ContainerDashboard.pattern,
                    },
                    {
                        title: t('CONTAINER_TYPES'),
                        id: ContainerManagementDomains.ContainerTypes,
                        auth: authRoles.container_read,
                        url: ContainerManagementRoutes.ContainerTypes.pattern,
                    },
                    {
                        title: t('SERVICE_CENTERS'),
                        id: ContainerManagementDomains.ServiceCenters,
                        auth: authRoles.service_center_read,
                        url: ContainerManagementRoutes.ServiceCenters.pattern,
                    },
                ],
            },
            {
                title: t('LOGGERS_AND_GATEWAYS'),
                id: 'loggers-and-gateways',
                subTitleText: t('LOGGERS_AND_GATEWAYS_SUBTITLE'),
                icon: SensorsIcon,
                links: [
                    {
                        title: t('LOGGERS'),
                        id: App.Loggers,
                        auth: authRoles.logger_read,
                        url: LoggerRoutes.pattern,
                    },
                    {
                        title: t('GATEWAYS'),
                        id: App.Gateways,
                        auth: authRoles.gateway_read,
                        url: GatewaysRoutes.pattern,
                    },
                    {
                        title: t('DEPLOYMENTS'),
                        id: App.Deployments,
                        auth: authRoles.gateway_read,
                        url: DeploymentRoutes.pattern,
                    },
                ],
            },
            {
                title: t('BILLING'),
                id: App.Finance,
                subTitleText: t('BILLING_SUBTITLE'),
                icon: FinanceIcon,
                className: 'iconMedium',
                links: [
                    {
                        title: t('INVOICES'),
                        id: FinanceDomains.Invoices,
                        auth: authRoles.outgoing_invoice_read,
                        url: FinanceRoutes.Invoices.pattern,
                    },
                    {
                        title: t('FX_RATES'),
                        id: FinanceDomains.FxRates,
                        auth: authRoles.fxrate_read,
                        url: FinanceRoutes.FxRates.pattern,
                    },
                ],
            },
            {
                title: t('TRANSPORT_PLANNER'),
                id: App.TransportPlanner,
                subTitleText: t('TRANSPORT_PLANNER_SUBTITLE'),
                icon: TransportPlannerIcon,
                className: 'iconMedium',
                links: [
                    {
                        title: t('NEW_TEMPERATURE_SIMULATION'),
                        id: TransportPlannerDomains.Simulation,
                        auth: authRoles.transport_planner_create,
                        url: TransportPlannerRoutes.NewSimulation.pattern,
                    },
                    {
                        title: t('SAVED_TEMPERATURE_SIMULATIONS'),
                        id: TransportPlannerDomains.SavedSimulations,
                        auth: authRoles.transport_planner_read,
                        url: TransportPlannerRoutes.SavedSimulations.pattern,
                    },
                    {
                        title: t('CO2_CALCULATION'),
                        id: TransportPlannerDomains.C02Calculation,
                        auth: authRoles.transport_planner_read,
                        url: TransportPlannerRoutes.CO2Calculation.pattern,
                    },
                ],
            },
            {
                title: t('DAMAGE_TRACKING'),
                id: App.DamageTracking,
                subTitleText: t('DAMAGE_TRACKING_SUBTITLE'),
                icon: DamagesIcon,
                className: 'iconMedium',
                links: [
                    {
                        title: t('DAMAGES'),
                        id: DamageTrackingDomains.Damages,
                        auth: authRoles.damage_tracking_read,
                        url: DamageTrackingRoutes.Damages.pattern,
                    },
                    {
                        title: t('DAMAGE_TYPES'),
                        id: DamageTrackingDomains.DamageTypes,
                        auth: authRoles.damage_types_read,
                        url: DamageTrackingRoutes.DamageTypes.pattern,
                    },
                ],
            },
            {
                title: t('RATE_CARDS'),
                id: App.Rates,
                subTitleText: t('RATE_CARDS_SUBTITLE'),
                icon: RateCardsIcon,
                className: 'iconMedium',
                links: [
                    {
                        title: t('A_RATE_CARDS'),
                        id: RatesDomains.ARateCards,
                        auth: authRoles.a_pricing_rates_read,
                        url: RatesRoutes.ARateCards.pattern,
                    },
                    {
                        title: t('C_RATE_CARDS'),
                        id: RatesDomains.CRateCards,
                        auth: authRoles.rates_read,
                        url: RatesRoutes.CRateCards.pattern,
                    },
                ],
            },
        ] as ActiveTab[]
    }, [
        AccountManagementRoutes,
        ContactManagementRoutes,
        ContainerManagementRoutes,
        DamageTrackingRoutes,
        DeploymentRoutes,
        FinanceRoutes,
        ForecastRoutes,
        GatewaysRoutes,
        LoggerRoutes,
        OrderManagementRoutes,
        PricingRoutes,
        RelationshipsManagementRoutes,
        TransportPlannerRoutes,
        RatesRoutes,
        t,
    ])
}
